import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { JurisdictionPrefixService, JurisdictionTranslatePrefix } from "@obg/ngx.core";
import { PlatformService } from "@obg/ngx.global";
import { isEmpty, map as lodashMap, merge, reduce } from "lodash-es";
import { forkJoin, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { ContentClient } from "../content/content.client";
import { CoreContentState } from "../state";

import { TranslationAction } from "./translation.action";
import { TranslationOptions } from "./translation.model";
import { TranslationSelector } from "./translation.selector";

/**
 * @deprecated - [fabric-deprecated]
 */
@Injectable({
	providedIn: "root",
})
export class TranslationService {
	constructor(
		private client: ContentClient,
		private service: TranslateService,
		private translationSelector: TranslationSelector,
		private jurisdictionPrefixService: JurisdictionPrefixService,
		private store: Store<CoreContentState>,
		private platformService: PlatformService
	) {}
	/**
	 * @deprecated - [fabric-deprecated]
	 */
	init(language: string) {
		this.service.currentLang = language;
		this.service.setDefaultLang(language);
		this.service.use(language);
	}

	/**
	 * @deprecated - [fabric-deprecated]
	 */
	set(translations: Record<string, string>) {
		this.service.setTranslation(this.service.currentLang, translations, true);

		/*
			This function is called on both the server and client sides. On the server side,
			the content loads faster, but on the client side, it takes longer.
			We should wait on client side till the content gets loaded, then we can use the translations.
		 */
		if (this.platformService.isBrowser) {
			this.store.dispatch(TranslationAction.setClientLoaded());
		}
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use documentRef.obgEventBus.retrieve(ContentValue.getTranslationGroups) instead
	 */
	getTranslationGroupByNames(groupNames: string[]): Observable<Record<string, string> | undefined> {
		if (isEmpty(groupNames)) {
			return of(undefined);
		}

		const translationsResults: Observable<Record<string, string>>[] = lodashMap(groupNames, (x) =>
			this.client.getComponentGroupByName(x)
		);

		return forkJoin(translationsResults).pipe(
			map((translations) => reduce(translations, merge) as Record<string, string>)
		);
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use TranslateService.instant instead
	 */
	getByKey(key: string | string[], interpolateParams?: object): string | any {
		return this.service.instant(key, interpolateParams);
	}

	/**
	 * @deprecated - [fabric-deprecated] - Use documentRef.obgEventBus.retrieve(ContentValue.getTranslationsByJurisdictionPrefix) instead
	 */
	translateByJurisdictionPrefixList$(options: TranslationOptions): Observable<(string | undefined)[]> {
		options.requiredGroups = options.requiredGroups ?? ["common", "general"];
		options.dotMapping = options.dotMapping ?? false;

		let jurisdictionTranslatePrefix: string | undefined;
		return this.jurisdictionPrefixService.getJurisdictionPrefix$().pipe(
			switchMap((jurisdiction) => {
				jurisdictionTranslatePrefix = jurisdiction;
				const keys = options.defaultCrsKeys.map((key) =>
					[JurisdictionTranslatePrefix.Mga, JurisdictionTranslatePrefix.Ksa].includes(
						jurisdiction as JurisdictionTranslatePrefix
					)
						? key
						: `${jurisdiction}.${key}`
				);
				return this.store.pipe(
					this.translationSelector.getByKeys_v2$(
						keys,
						options.requiredGroups as string[],
						options.interpolateParams
					)
				);
			}),
			map((keys) =>
				options.dotMapping
					? keys.map((key) => {
							if (!key || key.split(".")[0] !== jurisdictionTranslatePrefix) {
								return key;
							}

							const newKey = key.substring(key.indexOf(".") + 1);
							return this.getByKey(newKey, options.interpolateParams);
						})
					: keys
			)
		);
	}
}
